import {
  IHeaderConfig,
  IPTableColumnInfo,
  ITitle,
  TitleTypes
} from '@libs/common-components';
import { GeoTestMethod, GeoTestType } from '@libs/apis';
import { formatPercent } from '@angular/common';
import { GeoSplitType } from '@portal/app/dashboard/geo-doe-config/shared/constants';
import { DateTimeService } from '@portal/app/shared/services/date-time.service';
import { GeoCellDisplayStatus } from '@portal/app/geo-designer/models/ICPAdTestConfig';
import { ITestActionButton } from '@portal/app/geo-designer/models/ITestActionButton';
import { Confirmation, Message } from 'primeng/api';
import { GeoTestAction } from '@portal/app/geo-designer/models/GeoTestAction';
import { GeoTestBudgetLevel } from '@portal/app/geo-designer/common/geo-tests-field-definition';
import { ButtonType } from '@design-system/components/m-button';
import { GeoTestTacticType } from '../models/GeoTestComposition';
import { Dayjs } from 'dayjs';
import { ICellRequestState } from '../services/geo-test-market.service';
import { MimStatus } from '../services/test-calibration-comparison.service';

export class GeoDesignerConstants {
  public static readonly recommendedTestCardId = 'recommended-test-action-card';
  public static readonly demoClientId = 9995;
  public static readonly minStartDaysInFuture = 1;
  public static readonly geoTestDurationPaddingDays = 2;
  public static readonly daysInWeek = 7;
  public static readonly dateFormatLong = 'DD MMM, YYYY';
  public static readonly apiDateFormat = 'YYYY-MM-DD';
  public static readonly cellContaminationPaddingInDays = 16;
  public static readonly defaultReferenceDurationInDays = 30;
  public static readonly calendarMaxDateInMonths = 15;
  public static readonly defaultCalendarSelectionSizeInDays = 30;
  public static readonly defaultConcurrency = 2;
  public static readonly multiKpiSummaryRoasCpoFadeDelayInMillis = 500;
  public static readonly roasCpoSlideDurationInMillis = 400;

  public static readonly multipleConversionTypesLabel =
    'Multiple Conversion Types';

  public static readonly multipleKpiContributionLabel = 'Multiple';
  public static readonly noneFoundLabel = 'None found';
  public static readonly platformCompositeDataSourceId = {
    pinterest: 5,
    tiktok: 71,
    facebook: 1,
    adwords: 2
  };

  public static readonly designerVersion = {
    v1: 1,
    v2: 2
  };

  public static readonly headerConfig: IHeaderConfig = {
    primaryTitle: {
      defaultValue: 'Geo Tests',
      field: 'header',
      span: 12
    },
    faq: {
      field: 'https://support.measured.com/article/gaagjv18b5-geo-how-to',
      label: 'Get info about Geo Tests',
      span: 12
    },
    secondaryTitles: [],
    badges: []
  };

  public static readonly testTitleConfig: ITitle = {
    dataTracker: 'lib-ui-title__testOverview',
    title: '',
    styleClass: 'm-0 text-gray-1000',
    subTitle: null,
    type: TitleTypes.h4,
    ellipsisConfig: {
      title: {
        enabled: true,
        ellipsisMaxLength: 45
      }
    }
  };

  public static readonly allRecommendedTestsTitleConfigs: ITitle[] = [
    {
      title: 'Select a New Test',
      type: TitleTypes.h4,
      styleClass: 'mb-0 text-gray-1000',
      subTitle: null
    }
  ];

  public static readonly testTypeIcons: Record<GeoTestType, string> = {
    [GeoTestType.holdout]: 'block',
    [GeoTestType.scale]: 'stacked_line_chart',
    [GeoTestType.lift]: 'system_update_alt'
  };

  public static readonly testMethodIcons: Record<GeoTestMethod, string> = {
    [GeoTestMethod.auto]: 'smart_toy',
    [GeoTestMethod.manual]: 'front_hand'
  };

  public static readonly geoSplitTypeLabels: Record<string, string> = {
    [GeoSplitType.REGION]: 'States',
    [GeoSplitType.ZIP]: 'ZIP Codes',
    [GeoSplitType.OUTWARD_CODE_ZIP]: 'ZIP Codes',
    [GeoSplitType.DMA]: 'DMAs',
    [GeoSplitType.CITY]: 'Cities'
  };

  public static readonly scheduledTestMethodMessages: Record<
    GeoTestMethod,
    (date?: Dayjs | string) => string
  > = {
    [GeoTestMethod.auto]: (endDate?: Dayjs | string) =>
      `This test will launch automatically and changes will be switched back on ${DateTimeService.dayJsDate(
        endDate
      ).format(GeoDesignerConstants.dateFormatLong)}`,
    [GeoTestMethod.manual]: () => `This test cannot be launched automatically`
  };

  public static readonly recommendedTestMethodMessages: Record<
    GeoTestMethod,
    (date?: Dayjs | string) => string
  > = {
    [GeoTestMethod.auto]: (endDate?: Dayjs | string) =>
      `This test will launch automatically and changes will be switched back on ${DateTimeService.dayJsDate(
        endDate
      ).format(GeoDesignerConstants.dateFormatLong)}`,
    [GeoTestMethod.manual]: () => 'This test cannot be launched automatically'
  };

  public static readonly activeTestMethodMessages: Record<
    GeoTestMethod,
    (date?: Dayjs | string) => string
  > = {
    [GeoTestMethod.auto]: (endDate?: Dayjs | string) =>
      `This test is live. It's managed by Measured and the changes will be switched back on ${DateTimeService.dayJsDate(
        endDate
      ).format(GeoDesignerConstants.dateFormatLong)}`,
    [GeoTestMethod.manual]: (endDate?: Dayjs | string) =>
      `This test is managed by the advertiser. Changes will need to be reverted back on ${DateTimeService.dayJsDate(
        endDate
      ).format(GeoDesignerConstants.dateFormatLong)}`
  };

  public static readonly testActionButtons: ITestActionButton[] = [
    {
      label: 'Schedule',
      availableFor: [GeoCellDisplayStatus.none],
      style: ButtonType.primary,
      order: 1,
      action: GeoTestAction.saveAsActive,
      confirmationRequired: true
    },
    {
      label: 'Schedule',
      availableFor: [GeoCellDisplayStatus.actionNeeded],
      style: ButtonType.primary,
      order: 1,
      action: GeoTestAction.updateTestActive,
      confirmationRequired: true
    },
    {
      label: 'Save draft',
      availableFor: [GeoCellDisplayStatus.none],
      style: ButtonType.secondary,
      order: 2,
      action: GeoTestAction.saveAsDraft,
      confirmationRequired: true
    },
    {
      label: 'Save draft',
      availableFor: [
        GeoCellDisplayStatus.draft,
        GeoCellDisplayStatus.actionNeeded
      ],
      style: ButtonType.secondary,
      order: 2,
      action: GeoTestAction.updateTestDraft,
      confirmationRequired: true
    },
    {
      label: 'Revert to draft',
      availableFor: [GeoCellDisplayStatus.scheduled],
      style: ButtonType.secondary,
      order: 2,
      action: GeoTestAction.updateTestDraft,
      enabledAlways: true
    },
    {
      label: 'Schedule',
      availableFor: [GeoCellDisplayStatus.draft],
      style: ButtonType.primary,
      order: 2,
      action: GeoTestAction.updateTestActive,
      confirmationRequired: true,
      enabledAlways: true
    },
    {
      label: 'Download as PDF',
      availableFor: [
        GeoCellDisplayStatus.none,
        GeoCellDisplayStatus.scheduled,
        GeoCellDisplayStatus.draft,
        GeoCellDisplayStatus.inProgress
      ],
      style: ButtonType.tertiary,
      order: 3,
      action: GeoTestAction.downloadSummary,
      enabledAlways: true
    },
    {
      label: 'Download as PDF',
      availableFor: [
        GeoCellDisplayStatus.resultsPending,
        GeoCellDisplayStatus.pendingToLaunch,
        GeoCellDisplayStatus.finished
      ],
      style: ButtonType.primary,
      order: 3,
      action: GeoTestAction.downloadSummary,
      enabledAlways: true
    },
    {
      label: 'Remove test',
      availableFor: [
        GeoCellDisplayStatus.draft,
        GeoCellDisplayStatus.scheduled,
        GeoCellDisplayStatus.pendingToLaunch
      ],
      style: ButtonType.tertiary,
      order: 4,
      action: GeoTestAction.saveAsInactive,
      confirmationRequired: true,
      enabledAlways: true
    },
    {
      label: 'Cancel',
      availableFor: [GeoCellDisplayStatus.inProgress],
      style: ButtonType.secondary,
      order: 2,
      action: GeoTestAction.cancel,
      confirmationRequired: true
    },
    {
      label: 'Save',
      availableFor: [
        GeoCellDisplayStatus.scheduled,
        GeoCellDisplayStatus.inProgress,
        GeoCellDisplayStatus.resultsPending
      ],
      style: ButtonType.primary,
      order: 1,
      action: GeoTestAction.updateTest,
      confirmationRequired: true
    },
    {
      label: 'Export Data',
      availableFor: [GeoCellDisplayStatus.finished],
      style: ButtonType.secondary,
      order: 4,
      action: GeoTestAction.exportData,
      confirmationRequired: false
    }
  ];

  public static confirmations: Record<string, Confirmation> = {
    [GeoTestAction.saveAsActive]: {
      header: 'Confirmation Scheduling',
      message: `If you proceed, this test will be set to launch on the date you chose in its settings.`,
      acceptLabel: 'Schedule',
      rejectLabel: 'Cancel'
    },
    [GeoTestAction.updateTestActive]: {
      header: 'Confirmation Scheduling',
      message: `If you proceed, this test will be set to launch on the date you chose in its settings.`,
      acceptLabel: 'Schedule',
      rejectLabel: 'Cancel'
    },
    [GeoTestAction.updateTestDraft]: {
      header: 'Confirmation Save',
      message: `This will save the test as a draft, which will require further scheduling to launch.`,
      acceptLabel: 'Save Draft',
      rejectLabel: 'Cancel'
    },
    [GeoTestAction.saveAsDraft]: {
      header: 'Confirmation Save',
      message: `This will save the test as a draft, which will require further scheduling to launch.`,
      acceptLabel: 'Save Draft',
      rejectLabel: 'Cancel'
    },
    [GeoTestAction.updateTest]: {
      header: 'Confirmation Save',
      message: `Check your test settings before updating it there. Changing the end date may affect test results.`,
      acceptLabel: 'Save',
      rejectLabel: 'Cancel'
    },
    [GeoTestAction.cancel]: {
      header: 'Confirmation Reset',
      message: `You have unsaved changes. If you close the popup they will be reset.`,
      acceptLabel: 'OK',
      rejectLabel: 'Return'
    },
    [GeoTestAction.saveAsInactive]: {
      header: 'Confirmation Removal',
      message: `If you delete this, it will no longer be present on your schedule of upcoming tests.`,
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel'
    }
  };

  public static readonly businessRiskMessageBuilder: Record<
    GeoTestType,
    (businessRisk?: number) => string
  > = {
    [GeoTestType.holdout]: (businessRisk?: number): string =>
      `This list maximizes test value while minimizing business risk, which in this case is only ${formatPercent(
        businessRisk || 0,
        'en-US',
        '1.2-2'
      )}.`,
    [GeoTestType.lift]: (): string =>
      'This list is used to find out your media contribution in these regions.',
    [GeoTestType.scale]: (): string =>
      'This list maximizes value while minimizing the cost.'
  };

  public static readonly adEntityLabelMap: Record<GeoTestBudgetLevel, string> =
    {
      [GeoTestBudgetLevel.adset]: 'ad set',
      [GeoTestBudgetLevel.campaign]: 'campaign'
    };

  public static readonly testInstructionText: Record<string, string> = {
    stepsToLaunch: 'Steps to Launch the Test',
    whileTestIsRunning: 'While Your Test is Running',
    stepsToEnd: 'Steps to End the Test',
    operationDetails: 'Operation Details',
    whenYourTestBegins: 'When Your Test Begins',
    customerSuccessInstruction:
      'Your customer success partner will alert you if any changes require your attention',
    testCorruptionNotice:
      'Be sure not to launch any promotions or activity that would create non-standard business conditions',
    testAutomationNotice:
      'This process will be automated as much as possible, but some manual action may be necessary'
  };

  public static readonly testAnchorMarketDescription: string = `These will not be tested, but they will be used as the basis for the
            predicted orders in your test markets.`;

  public static readonly updatedConversionTypesWithCellsDescription: Message[] =
    [
      {
        severity: 'info',
        summary: '',
        detail:
          'The list of conversion types has changed, so the test markets cell has been changed to the most optimal one.'
      }
    ];

  public static readonly updatedConversionTypesWithoutCellsDescription: Message[] =
    [
      {
        severity: 'info',
        summary: '',
        detail:
          'The list of conversion types has changed, so you must select a cell or request its creation.'
      }
    ];

  public static readonly geoCellEmptyState: Partial<ICellRequestState>[] = [
    {
      emptyStateTitle: 'You have not created any cells for this multi-KPI test',
      emptyStateDescription:
        'Submit a request for cells that combine your chosen conversion types.<br>Cell creation may take up to 48 hours.'
    },
    {
      emptyStateTitle: 'Your test cell request is being processed',
      emptyStateDescription:
        'This may take up to 48 hours. You will be notified when they are ready.<br>Then you can approve them for your upcoming tests.'
    },
    {
      emptyStateTitle: 'This request is already in progress',
      emptyStateDescription:
        'A request for test cells containing these KPIs has been filed.<br>You will be notified when they are ready.'
    }
  ];

  public static readonly geoSplitTypeAbbreviations: Record<string, string> = {
    [GeoSplitType.REGION]: 'states',
    [GeoSplitType.ZIP]: 'zip codes',
    [GeoSplitType.OUTWARD_CODE_ZIP]: 'zip codes',
    [GeoSplitType.DMA]: 'designated market areas',
    [GeoSplitType.CITY]: 'cities'
  };

  public static readonly testMetricColumns: IPTableColumnInfo[] = [
    {
      field: 'metric',
      header: 'Metric',
      width: '15rem',
      class: 'min-w-[15rem] min-h-[4.5rem]'
    },
    {
      field: 'description',
      header: 'Description',
      width: '30rem',
      class: 'min-w-[30rem] h-[4.5rem] text-gray-800'
    }
  ];

  public static readonly heroMetricIds = [
    'contribution',
    'incrementality',
    'roasI',
    'cpaI'
  ];

  public static readonly heroMetrics = {
    contribution: 'contribution',
    incrementality: 'incrementality',
    roasI: 'roasI',
    cpaI: 'cpaI',
    predictedOrders: 'predictedOrders',
    predictedSales: 'predictedSales',
    ordersI: 'ordersI',
    mediaSpend: 'mediaSpend',
    marginalSpend: 'marginalSpend',
    normalSpend: 'normalSpend',
    totalSpend: 'totalSpend',
    revenue: 'revenue',
    revenueI: 'revenueI',
    confidenceLevel: 'confidenceLevel',
    testDurationInWeeks: 'testDurationInWeeks',
    tactics: 'tactics',
    scaleFactor: 'scaleFactor',
    scaleCost: 'scaleCost',
    ordersActual: 'ordersActual',
    conversionType: 'conversionType',
    conversions: 'conversions',
    aov: 'aov',
    orderDifference: 'orderDifference',
    contributionLowerBound: 'contributionLowerBound',
    contributionUpperBound: 'contributionUpperBound',
    incrementalityLowerBound: 'incrementalityLowerBound',
    incrementalityUpperBound: 'incrementalityUpperBound',
    roasILowerBound: 'roasILowerBound',
    roasIUpperBound: 'roasIUpperBound',
    cpaILowerBound: 'cpaILowerBound',
    cpaIUpperBound: 'cpaIUpperBound'
  };

  public static readonly tacticTypeLabel: Record<string, string> = {
    [GeoTestTacticType.singleTactic]: 'Single-Tactic',
    [GeoTestTacticType.multiTactic]: 'Multi-Tactic'
  };

  public static readonly howItWorksHelpDocLink =
    'https://support.measured.com/article/gaagjv18b5-geo-how-to';

  public static readonly geoResultsLearnMoreDocLink =
    'https://support.measured.com/article/x8uob1vaxa-geo-results';

  public static readonly multiKpiSummaryViewColumnsWithContributionOnly: IPTableColumnInfo[] =
    [
      {
        field: 'conversionType',
        header: 'Conversion Type',
        width: '25%',
        class: 'b2'
      },
      {
        field: 'contribution',
        header: 'Contribution',
        width: '20%',
        class: 'b1'
      },
      {
        field: 'predictedConversions',
        header: 'Predicted Conversions',
        width: '20%',
        class: 'b1'
      },
      {
        field: 'actualConversions',
        header: 'Actual Conversions',
        width: '20%',
        class: 'b1'
      },
      {
        field: 'incrementalConversions',
        header: 'Incremental Conversions',
        width: '20%',
        class: 'b1'
      }
    ];

  public static readonly multiKpiSummaryViewBaseColumns: IPTableColumnInfo[] = [
    {
      field: 'conversionType',
      header: 'Conversion Type',
      width: '19%',
      class: 'b2'
    },
    {
      field: 'contribution',
      header: 'Contribution',
      width: '10%',
      class: 'b1'
    },
    {
      field: 'testAdjustmentFactor',
      header: 'Test Adjustment Factor',
      width: '16%',
      class: 'b1'
    }
  ];

  public static readonly multiKpiSummaryViewRoasColumns: IPTableColumnInfo[] = [
    {
      field: 'incrementalROAS',
      header: 'Incremental ROAS',
      width: '14%',
      class: 'b2'
    },
    {
      field: 'roasIConfidenceInterval',
      header: 'Confidence Interval',
      width: '27%'
    }
  ];

  public static readonly multiKpiSummaryViewCpoColumns: IPTableColumnInfo[] = [
    {
      field: 'incrementalCPO',
      header: 'Incremental CPO',
      width: '14%',
      class: 'b2'
    },
    {
      field: 'cpoIConfidenceInterval',
      header: 'Confidence Interval',
      width: '27%'
    }
  ];

  public static readonly multiKpiSummaryViewStatisticalSignificanceColumn: IPTableColumnInfo =
    {
      field: 'confidenceLevel',
      header: 'Statistical Significance',
      width: '15%',
      class: 'b1'
    };

  public static readonly multiKpiSummaryViewColumnsWithoutConfidenceInterval: IPTableColumnInfo[] =
    [
      { field: 'conversionType', header: 'Conversion Type', width: '20%' },
      { field: 'contribution', header: 'Contribution', width: '15%' },
      {
        field: 'testAdjustmentFactor',
        header: 'Test Adjustment Factor',
        width: '20%'
      },
      { field: 'incrementalROAS', header: 'Incremental ROAS', width: '20%' },
      { field: 'incrementalCPO', header: 'Incremental CPO', width: '20%' }
    ];

  public static readonly calibrationMessages = {
    [MimStatus.INELIGIBLE]: {
      title: 'This test is ineligible for your reporting.',
      message:
        'Visit the Tests tab of your MIM page and review the Status column to learn why it is ineligible for inclusion.'
    },
    [MimStatus.ELIGIBLE]: {
      included: {
        title: 'This test has been included in your reporting',
        message:
          'Visit your MIM page to see or change how any available test results are contributing to your model.'
      },
      excluded: {
        title: 'This test has been excluded from your reporting.',
        message:
          'Visit your MIM page to see how results from this and other available tests would affect your model.'
      }
    },
    [MimStatus.IN_PROGRESS]: {
      title: 'This test may be eligible for your reporting',
      message:
        'These results are not finalized yet, but you can see their impact to your model on your MIM page.'
    }
  };
}
